import {
  type CallWindow,
  type CallWindowFilter,
  type CallWindowList,
  type CallWindowSort,
  type LoadCallWindowsRequest,
} from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

import { CallWindowClientService } from '../../tools/helpers';
import { queryKeys } from './constants';

export const getCallWindowQueryConfig = (filter: CallWindowFilter) => {
  const sort = {
    orderBy: 'created_at',
    orderDir: 'DESC' as const,
  };
  return {
    queryFn: async () => {
      const req: LoadCallWindowsRequest = {
        page: 1,
        filter,
        sort,
      };
      return await CallWindowClientService.loadCallWindows(req);
    },
    queryKey: [
      queryKeys.callWindow.root,
      queryKeys.callWindow.list,
      queryKeys.callWindow.getHash(filter),
      sort,
      1,
    ],
  };
};

export const useCallWindowQuery = ({
  filter,
  enabled = true,
  options,
}: {
  filter: CallWindowFilter;
  enabled?: boolean;
  options?: Partial<UseQueryOptions<CallWindowList, Error>>;
}) => {
  return useQuery({
    ...getCallWindowQueryConfig(filter),
    enabled,
    ...options,
  });
};

export const useCallWindowBatchQuery = ({
  filter,
  sort,
  page = -1,
  enabled = true,
  options,
}: {
  filter: CallWindowFilter;
  sort: CallWindowSort;
  page?: number;
  enabled?: boolean;
  options?: Partial<UseQueryOptions<CallWindowList, Error>>;
}) => {
  const queryConfig = {
    queryFn: async () => {
      const req: LoadCallWindowsRequest = {
        page,
        filter,
        sort,
      };
      return await CallWindowClientService.loadCallWindows(req);
    },
    queryKey: [
      queryKeys.callWindow.root,
      queryKeys.callWindow.list,
      queryKeys.callWindow.getHash(filter),
      sort,
      page,
    ],
  };

  return useQuery({
    ...queryConfig,
    enabled,
    ...options,
  });
};

export const useCallWindowGetQuery = ({
  id,
  enabled = true,
  options,
}: {
  id: number;
  enabled?: boolean;
  options?: Partial<UseQueryOptions<CallWindow, Error>>;
}) => {
  return useQuery({
    queryFn: async () => {
      const req = {
        id,
        dayOfWeek: 0,
        startTime: '',
        endTime: '',
        windowCount: 0,
        createdAt: '',
        updatedAt: '',
        isActive: true,
        fieldMask: [],
        pageNumber: 0,
        orderBy: '',
        orderDir: '',
        label: '',
        departmentId: 0,
        maintMax: -1,
        eventTradeId: 0,
      };
      const result = await CallWindowClientService.Get(req);
      if (!result) {
        throw new Error('CallWindow not found');
      }
      return result;
    },
    queryKey: [queryKeys.callWindow.root, 'get', id],
    enabled: enabled && id > 0,
    ...options,
  });
};

export const useCallWindowCreateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (callWindow: Omit<CallWindow, 'id' | 'createdAt' | 'updatedAt'>) => {
      const req = {
        id: 0,
        createdAt: '',
        updatedAt: '',
        ...callWindow,
      };
      return await CallWindowClientService.Create(req);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.callWindow.root],
      });
    },
  });
};

export const useCallWindowUpdateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (callWindow: CallWindow) => {
      return await CallWindowClientService.Update(callWindow);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.callWindow.root],
      });
      if (data?.id) {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.callWindow.root, 'get', data.id],
        });
      }
    },
  });
};

export const useCallWindowDeleteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const req = {
        id,
        dayOfWeek: 0,
        startTime: '',
        endTime: '',
        windowCount: 0,
        createdAt: '',
        updatedAt: '',
        isActive: true,
        fieldMask: [],
        pageNumber: 0,
        orderBy: '',
        orderDir: '',
        label: '',
        departmentId: 0,
        maintMax: -1,
        eventTradeId: 0,
      };
      return await CallWindowClientService.Delete(req);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.callWindow.root],
      });
    },
  });
};

// Types for exports
export type CallWindowQueryFilter = CallWindowFilter;
export type CallWindowQuerySort = CallWindowSort;
export type CallWindowQueryRequest = LoadCallWindowsRequest;
